/**
 * Timer — countdown for the active decision per PRD §8.2 + wireframe 04.
 *
 * Renders TWO elements: a numeric seconds readout AND a thin horizontal
 * countdown bar that depletes from full to empty as time runs out (8px
 * tall in the mockups). The bar fills the horizontal space between the
 * numeric on the left and whatever sits at the row's right edge (REC
 * indicator etc.) — the parent's flex layout owns the spacing.
 *
 * Behavior:
 *   - Counts down from the session preset (60/45/30/15s).
 *   - Resets each time it becomes hero's turn (resetKey changes).
 *   - When 10 seconds remain, the numeric becomes "high-visibility" (red
 *     + bold + larger) and the bar fill switches to the same red.
 *   - When 0 reached: stays at 0, does NOT force action. The grade prompt
 *     records final time elapsed; the engine doesn't auto-fold (PRD §8.2
 *     step 5).
 *   - When not active (villain to act or hand terminal), numeric is greyed
 *     and the bar is shown at full but in a muted color.
 *   - When `initialSeconds === null` ("no timer" session preset), the
 *     component renders nothing.
 */

import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface TimerProps {
  /** Preset seconds from session config. Null means "no timer" → returns null. */
  initialSeconds: number | null;
  /** True while hero is to act on an open street. */
  active: boolean;
  /**
   * Changes whenever the timer should reset (e.g., new hand, new street, new
   * action). Typical caller value: `state.history.length` + a hand-id suffix.
   */
  resetKey: string | number;
}

export function Timer({ initialSeconds, active, resetKey }: TimerProps) {
  const [seconds, setSeconds] = useState(initialSeconds ?? 0);

  // Reset on resetKey or initialSeconds change
  useEffect(() => {
    if (initialSeconds === null) return;
    setSeconds(initialSeconds);
  }, [initialSeconds, resetKey]);

  // Tick while active
  useEffect(() => {
    if (!active || initialSeconds === null) return;
    const id = setInterval(() => {
      setSeconds((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [active, initialSeconds]);

  if (initialSeconds === null) return null;

  const lowTime = seconds <= 10;
  const pct = Math.max(0, Math.min(1, seconds / initialSeconds));

  return (
    <div
      className="flex flex-1 items-center gap-2"
      aria-label={`Timer: ${seconds} seconds`}
    >
      {/* Numeric readout */}
      <div
        className={cn(
          'inline-flex shrink-0 items-baseline gap-0.5 font-mono text-sm tabular-nums',
          active
            ? lowTime
              ? 'font-bold text-state-error'
              : 'font-semibold text-text-primary'
            : 'text-text-muted',
        )}
      >
        <span className={cn(active && lowTime ? 'text-lg' : 'text-base')}>
          {seconds}
        </span>
        <span className="text-[10px] uppercase tracking-wider">s</span>
      </div>
      {/* Countdown bar — fills available horizontal space */}
      <div
        className="relative h-2 flex-1 overflow-hidden rounded-full bg-surface-elevated"
        role="progressbar"
        aria-valuenow={seconds}
        aria-valuemin={0}
        aria-valuemax={initialSeconds}
      >
        <div
          className={cn(
            'absolute inset-y-0 left-0 transition-[width] duration-1000 ease-linear',
            active
              ? lowTime
                ? 'bg-state-error'
                : 'bg-brand-accent'
              : 'bg-border-strong',
          )}
          style={{ width: `${pct * 100}%` }}
        />
      </div>
    </div>
  );
}
